import moment from 'moment'
import _ from 'lodash'
import { AxiosError } from 'axios'
import {
  ERR_CODE_UNEXPECTED,
  ERR_CODE_CLIENT_VALID,
  ERR_CODE_ABORT_SESSION,
  ERR_CODE_BAD_DATA_STRUCTURE,
  ERR_CODE_DATA_LOAD,
  ERR_CODE_SERVER_VALID
} from '@/tcw/constant/ErrorCode'
import { InvalidField, InvalidFields } from '@/components/common/form/types'

export class TCWBaseError extends Error {
  protected errorCode: string
  protected occurredDate: Date | undefined
  protected e?: Error
  constructor(errorCode: string, message: string, e?: Error) {
    super(message)
    this.name = new.target.name
    this.errorCode = errorCode
    this.occurredDate = new Date()
    this.e = e
    // // 下記の行はTypeScriptの出力ターゲットがES2015より古い場合(ES3, ES5)のみ必要
    // Object.setPrototypeOf(this, new.target.prototype);
  }

  public getErrorCode(): string {
    return this.errorCode
  }

  public outputMessage(): string {
    const dateStr = moment(this.occurredDate).format('YYYY/MM/DD HH:mm:ss')
    return `[${this.errorCode}] ${this.message} (${dateStr})`
  }

  public get stack(): string | undefined {
    return this.e?.stack
  }
}

// 予期せぬエラー
export class TCWUnexpectedError extends TCWBaseError {
  constructor(message: string, e?: Error) {
    super(ERR_CODE_UNEXPECTED, message, e)
  }
}

// TCardWEB API呼び出し時エラー
export class TCWApiError extends TCWBaseError {
  private statusCode: string | number | undefined
  private messageDetail: string | undefined

  public getStatusCode(): number {
    const statusCode = Number(this.statusCode)
    return _.isNaN(statusCode) ? 0 : statusCode
  }
  public getMessageDetail(): string | undefined {
    return this.messageDetail
  }

  constructor(errorCode: string, error: AxiosError) {
    const errorResponse = error?.response
    const errorBody = errorResponse?.data
    super(errorCode, errorBody.message, error)

    this.statusCode = errorResponse?.status ? errorResponse?.status : ''
    this.messageDetail = errorBody?.messageDetail
  }
}

// TCardWEB セッション切れエラー
// TODO 実装
export class TCWSessionError extends TCWBaseError {
  constructor(message?: string, e?: Error) {
    const m = message ? message : 'セッションが切れました'
    super(ERR_CODE_ABORT_SESSION, m, e)
  }
}

// TCardWEB クライアント入力チェックエラー
export class TCWClientValidationError extends TCWBaseError {
  private invalidFields: InvalidFields = {}
  constructor(message: string, invalidFields: InvalidFields) {
    super(ERR_CODE_CLIENT_VALID, message)
    this.invalidFields = invalidFields
  }

  public get invalidInfo(): InvalidFields {
    return this.invalidFields
  }

  public outputMessage(): string {
    const commonMessage = super.outputMessage()
    const fieldMessages = Object.keys(this.invalidFields).map((field: string) => {
      const invalidInfo = this.invalidFields[field]
      return `${invalidInfo.field}: ${invalidInfo.message}`
    })
    return commonMessage + '\r' + fieldMessages.join('\r')
  }
}

// TCardWEB サーバサイド入力チェックエラー
export class TCWServerValidationError extends TCWApiError {
  private invalidFields: InvalidField[]
  public getInvalidFieldList(): InvalidField[] | undefined {
    return this.invalidFields
  }

  constructor(error: AxiosError) {
    super(ERR_CODE_SERVER_VALID, error)
    this.invalidFields = error?.response?.data?.invalidFieldList
  }

  public get invalidInfo(): InvalidField[] {
    return this.invalidFields
  }

  public get invalidMessageList(): string[] {
    return this.invalidFields.map(f => f.message)
  }
}

// データ構造不正エラー
export class TCWBadDataStructureError extends TCWBaseError {
  constructor(message?: string, e?: Error) {
    const m = message ? message : '不正なプロパティアクセスです'
    super(ERR_CODE_BAD_DATA_STRUCTURE, m, e)
  }
}
// データ未ロードエラー
export class TCWDataLoadError extends TCWBaseError {
  constructor(message?: string, e?: Error) {
    const m = message ? message : 'データが未ロードです'
    super(ERR_CODE_DATA_LOAD, m, e)
  }
}
